import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { subDays } from "date-fns";
import AppBarMenu from "../components/AppBarMenu";
import { useDashboardStats } from "../hooks/api/dashboardApiHooks";
import { DashboardFilters } from "../services/api/dashboard/dashboardApi";

// Componentes de filtros
import DateRangeFilter from "../components/dashboard/filters/DateRangeFilter";
import ResponsibleFilter from "../components/dashboard/filters/ResponsibleFilter";
import ProvidenceFilter from "../components/dashboard/filters/ProvidenceFilter";
import ClientFilter from "../components/dashboard/filters/ClientFilter";
import PdcFilter from "../components/dashboard/filters/PdcFilter";

// Componentes de métricas
import MetricsGrid from "../components/dashboard/metrics/MetricsGrid";

// Componentes de gráficos
import TimeSeriesChart from "../components/dashboard/charts/TimeSeriesChart";
import ResponsibleChart from "../components/dashboard/charts/ResponsibleChart";
import ProvidenceChart from "../components/dashboard/charts/ProvidenceChart";
import DistributionChart from "../components/dashboard/charts/DistributionChart";

// Componentes de tabelas
import ProvidenceTable from "../components/dashboard/tables/ProvidenceTable";
import ResponsibleTable from "../components/dashboard/tables/ResponsibleTable";

export default function ActivitiesAnalysisPage() {
  // Estado dos filtros
  const [filters, setFilters] = useState<DashboardFilters>({
    startDate: subDays(new Date(), 30), // Últimos 30 dias
    endDate: new Date(),
    userIds: [],
    providenceIds: [],
    clientId: undefined,
    pdcId: undefined,
  });

  // Estado para controlar o accordion de filtros
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Hook para buscar dados
  const { data, isLoading, error, refetch } = useDashboardStats(filters);

  // Auto-refresh quando filtros mudam
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      refetch();
    }
  }, [filters, refetch]);

  // Handlers de filtros
  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setFilters((prev) => ({ ...prev, startDate, endDate }));
  };

  const handleResponsibleChange = (userIds: number[]) => {
    setFilters((prev) => ({ ...prev, userIds }));
  };

  const handleProvidenceChange = (providenceIds: number[]) => {
    setFilters((prev) => ({ ...prev, providenceIds }));
  };

  const handleClientChange = (clientId: number | undefined) => {
    setFilters((prev) => ({ ...prev, clientId }));
  };

  const handlePdcChange = (pdcId: number | undefined) => {
    setFilters((prev) => ({ ...prev, pdcId }));
  };

  const handleFiltersToggle = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Preparar dados para gráficos de distribuição
  const clientDistributionData = data?.byClient.map((item) => ({
    name: item.clientName,
    value: item.count,
  }));

  const pdcDistributionData = data?.byPdc.map((item) => ({
    name: item.pdcName,
    value: item.count,
  }));

  return (
    <AppBarMenu icons={null}>
      <Box sx={{ 
        backgroundColor: "#F7F8FA", 
        minHeight: "100vh",
        py: 3
      }}>
        <Container maxWidth="xl">
          {/* Cabeçalho */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight={700} 
              color="text.primary"
              sx={{ 
                fontSize: "1.75rem",
                lineHeight: 1.2
              }}
            >
              Análise de Atividades
            </Typography>
          </Box>

          {/* Mensagem de erro */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Erro ao carregar dados: {error.message}
            </Alert>
          )}

          {/* Painel Principal Unificado */}
          <Paper 
            sx={{ 
              backgroundColor: "#FFFFFF",
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              position: "relative"
            }}
          >
            {/* Loading Overlay */}
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={48} />
              </Box>
            )}

            {/* Seção de Filtros - Cabeçalho Separado Visualmente */}
            <Box 
              sx={{ 
                backgroundColor: "#F9FAFB",
                borderBottom: "2px solid #E5E7EB"
              }}
            >
              <Accordion 
                expanded={filtersExpanded} 
                onChange={handleFiltersToggle}
                sx={{ 
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  "&:before": { display: "none" }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    px: 4,
                    py: 2,
                    "&.Mui-expanded": {
                      minHeight: "auto",
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                      },
                    },
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: "1.125rem",
                      color: "text.primary"
                    }}
                  >
                    Filtros
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 4, pb: 3, pt: 0 }}>
                  <Grid container spacing={3}>
                    {/* Filtro de Data */}
                    <Grid item xs={12} md={6}>
                      <DateRangeFilter
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onChange={handleDateRangeChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Filtros adicionais */}
                    <Grid item xs={12} md={6}>
                      <ResponsibleFilter
                        value={filters.userIds || []}
                        onChange={handleResponsibleChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ProvidenceFilter
                        value={filters.providenceIds || []}
                        onChange={handleProvidenceChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ClientFilter
                        value={filters.clientId}
                        onChange={handleClientChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <PdcFilter
                        value={filters.pdcId}
                        onChange={handlePdcChange}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Área de Conteúdo - Fundo Branco */}
            <Box sx={{ p: 4 }}>
              {/* Seção: Indicadores de Performance */}
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 3
                  }}
                >
                  Indicadores de Performance
                </Typography>
                <MetricsGrid summary={data?.summary} loading={isLoading} />
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Seção: Volume de Atividades ao Longo do Tempo */}
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 3
                  }}
                >
                  Volume de Atividades ao Longo do Tempo
                </Typography>
                <TimeSeriesChart filters={filters} />
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Seção: Análise por Responsáveis e Providências */}
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 3
                  }}
                >
                  Análise por Responsáveis e Providências
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <ResponsibleChart data={data?.byResponsible} loading={isLoading} />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <ProvidenceChart data={data?.byProvidence} loading={isLoading} />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Seção: Distribuição por Cliente e Concessionária */}
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 3
                  }}
                >
                  Distribuição por Cliente e Concessionária
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <DistributionChart
                      data={clientDistributionData}
                      loading={isLoading}
                      title="Distribuição por Cliente"
                      maxItems={10}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <DistributionChart
                      data={pdcDistributionData}
                      loading={isLoading}
                      title="Distribuição por Concessionária"
                      maxItems={10}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Seção: Tabelas Detalhadas */}
              <Box>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 3
                  }}
                >
                  Tabelas Detalhadas
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <ProvidenceTable data={data?.byProvidence} loading={isLoading} />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <ResponsibleTable data={data?.byResponsible} loading={isLoading} />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </AppBarMenu>
  );
}

